/// <amd-module name="Artifacts/FeedSettings/helpers/UpstreamHelpers" />

import * as Resources from "Artifacts/FeedSettings/Resources";
import { IFeedSettings, IUpstreamSource } from "Artifacts/FeedSettings/types";
import { IProtocol } from "Artifacts/Root/protocols/types";
import { Feed, FeedView, UpstreamSourceType } from "Artifacts/Root/web-apis/contracts/Feed";
import { announce } from "VSS/Core/Util/Accessibility";
import * as StringUtils from "VSS/Core/Util/String";
import { Uri } from "VSS/Platform/Util/Url";
import { IListSelection } from "VSSUI/List";

export const MAX_UPSTREAM_LENGTH = 30;

export const getUpstreamSourceTypeName = (upstreamSourceType: UpstreamSourceType): string => {
  switch (upstreamSourceType) {
    case UpstreamSourceType.Public:
      return Resources.UpstreamsTab_UpstreamType_Public;
    case UpstreamSourceType.Internal:
      return Resources.UpstreamsTab_UpstreamType_Internal;
    default:
      throw new Error("Trying to resolve name for invalid upstream source type!");
  }
};

export const deleteUpstreamSources = (
  upstreamSelection: IListSelection,
  upstreamsList: IUpstreamSource[],
): IUpstreamSource[] => {
  const newList = upstreamsList.filter(
    (source, index) =>
      upstreamSelection.value.findIndex((range) => {
        return range.beginIndex <= index && index <= range.endIndex;
      }) === -1,
  );

  const deletedUpstreamNames: string[] = [];
  upstreamsList.forEach((u) => {
    if (!newList.some((newU) => newU.name === u.name)) {
      return deletedUpstreamNames.push(u.name);
    }
  });

  announce(
    StringUtils.format(
      Resources.UpstreamsTab_Deleted_Announcement,
      upstreamSelection.selectedCount,
      deletedUpstreamNames,
    ),
  );
  upstreamSelection.clear();
  return newList;
};

export const moveUpstreamSourceUp = (
  upstreamSelection: IListSelection,
  upstreamsList: IUpstreamSource[],
): IUpstreamSource[] => {
  if (upstreamSelection.selectedCount !== 1) {
    throw new Error(
      `tried to move ${upstreamSelection.selectedCount} upstreams, but only support moving 1`,
    );
  }

  const updatedList: IUpstreamSource[] = [];
  const i = upstreamSelection.value[0].beginIndex;
  const selectedUpstreamSourceName = upstreamsList[i].name;

  if (i < 1) {
    announce(Resources.UpstreamsTab_MoveUpAttempt_Announcement);
    return upstreamsList;
  }

  upstreamsList.forEach((upstream, j) => {
    if (j === i - 1) {
      updatedList.push(upstreamsList[i]);
    } else if (j === i) {
      updatedList.push(upstreamsList[i - 1]);
    } else {
      updatedList.push(upstream);
    }
  });
  upstreamSelection.select(i - 1);

  announce(
    StringUtils.format(Resources.UpstreamsTab_MovedUp_Announcement, selectedUpstreamSourceName, i),
  );
  return updatedList;
};

export const moveUpstreamSourceDown = (
  upstreamSelection: IListSelection,
  upstreamsList: IUpstreamSource[],
): IUpstreamSource[] => {
  if (upstreamSelection.selectedCount !== 1) {
    throw new Error(
      `tried to move ${upstreamSelection.selectedCount} upstreams, but only support moving 1`,
    );
  }

  const i = upstreamSelection.value[0].beginIndex;
  const selectedUpstreamSourceName = upstreamsList[i].name;

  if (i === upstreamsList.length - 1) {
    announce(Resources.UpstreamsTab_MoveDownAttempt_Announcement);
    return upstreamsList;
  }

  const updatedList: IUpstreamSource[] = new Array(...upstreamsList);
  // copy the upstream down in the list
  updatedList.splice(i, 1, upstreamsList[i + 1]);
  // fill the moved upstream's old space with the one below it
  updatedList[i + 1] = upstreamsList[i];

  upstreamSelection.select(i + 1);

  const newRowNumber = i + 2;
  announce(
    StringUtils.format(
      Resources.UpstreamsTab_MovedDown_Announcement,
      selectedUpstreamSourceName,
      newRowNumber,
    ),
  );
  return updatedList;
};

export function subtractUpstreams(
  upstreams: IUpstreamSource[] | undefined,
  upstreamsToRemove: IUpstreamSource[] | undefined,
): IUpstreamSource[] {
  return (
    upstreams?.filter(
      (upstream) =>
        upstreamsToRemove?.findIndex(
          (u) =>
            u.id === upstream.id ||
            (u.location === upstream.location && u.protocol === upstream.protocol),
        ) === -1,
    ) || []
  );
}

export function filterCurrentFeed(
  feeds: Feed[] | undefined,
  settings: IFeedSettings | undefined,
): Feed[] {
  return feeds?.filter((feed) => feed.id !== settings?.feedId) || [];
}

export function isViewAlreadyInUpstreamsForAllProtocols(
  view: FeedView,
  protocols: IProtocol[],
  upstreams?: IUpstreamSource[],
): boolean {
  return (
    upstreams?.filter((upstream) => upstream.internalUpstreamViewId === view.id).length ===
    protocols.length
  );
}

export function suggestedUpstreamName(feed: Feed, view: FeedView): string {
  if (feed && view) {
    return feed.name + "@" + view.name;
  }
  return "";
}

export function suggestedUpstreamNameForAadLocator(aadLocation: string): string {
  // This is duplicated logic from the hook. We need all of this validation before we can even suggest a name.
  const uri = new Uri(aadLocation);
  const matches = uri.path.match(/\/*([^\/]*)\/?([^\/]*)@([^\/]+)\/*(.*)/i);
  if (
    !uri ||
    uri.scheme.length === 0 ||
    uri.path.length === 0 ||
    uri.path.indexOf("@") < 0 ||
    !matches ||
    matches[4].length > 0
  ) {
    return "";
  }

  let host = uri.host;
  let view = matches[3];
  let project = matches[2] === "" ? matches[2] : matches[1];
  let feed = matches[2] === "" ? matches[1] : matches[2];

  const currentLength = host.length + project.length + feed.length + view.length;
  const maxLength = MAX_UPSTREAM_LENGTH - 3;
  if (currentLength > maxLength) {
    const sectionLength = maxLength / (3 + (project ? 1 : 0));
    host = host.substring(0, sectionLength);
    project = project.substring(0, sectionLength);
    feed = feed.substring(0, sectionLength);
    view = view.substring(0, sectionLength);
  }

  return project ? `${host}-${project}-${feed}@${view}` : `${host}-${feed}@${view}`;
}

/**
 * Compare locations without regard for url protocol
 */
export function upstreamLocationsEqual(a: string, b: string): boolean {
  const aUri = new Uri(a);
  const bUri = new Uri(b);
  aUri.scheme = "http";
  bUri.scheme = "http";
  return StringUtils.equals(aUri.absoluteUri, bUri.absoluteUri, true /*ignoreCase*/);
}
